import { useRef, useState } from 'react';
import { config } from '../config';
import { atLeast, useVendorAccess, LEVEL_LABEL } from './settings/vendorAccess';
import { VendorsPanel } from './settings/VendorsPanel';
import { ExpiringView } from './settings/ExpiringView';

/**
 * BUILD-043 - the dedicated UniqueHQ "Vendors" page (/vendors), reached from the Workspace sidebar directly below HR. It uses the whole content area (no Settings secondary menu)
 * and reuses the existing Vendors directory, drawer, services and records unchanged.
 *
 * Three folder-style tabs on one page: Vendors (the directory), Expirations (licenses and insurance across every vendor; a row opens the vendor on that record) and Admin (setup that is
 * not day-to-day, today the work-scope management panel).
 *
 * WHAT YOU SEE FOLLOWS WHAT YOU HAVE BEEN GIVEN. The server tells this page which Vendors level the signed-in Employee holds right now (none / View / Maintain / Administer; a Company Admin
 * is always Administer) and this page only SHOWS accordingly: View sees the Vendors tab (look-up only); Maintain adds Expirations and the editing controls; Administer adds the Admin
 * tab and status changes (including many at once); only a Company Admin sees who has access. That is display convenience only: every call is authorized again by the server on every
 * request, from the person's current grant, and it refuses anything this page would not have offered.
 */
type View = 'vendors' | 'expiring' | 'admin';
const ALL_TABS: Array<{ id: View; label: string; need: 'view' | 'maintain' | 'administer' }> = [{ id: 'vendors', label: 'Vendors', need: 'view' }, { id: 'expiring', label: 'Expirations', need: 'maintain' }, { id: 'admin', label: 'Admin', need: 'administer' }];

export function VendorsPage() {
  const { me, loading, unavailable } = useVendorAccess();
  const [view, setView] = useState<View>('vendors');
  const [link, setLink] = useState<{ vendorId: string; coverageId: string; n: number } | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  if (loading) {
    return <div className="p-6" data-testid="vendors-page"><p className="text-sm text-gray-500" data-testid="vendors-loading">Loading…</p></div>;
  }
  if (me.level === 'none') {
    return (
      <div className="p-6 max-w-3xl" data-testid="vendors-page">
        <h2 className="text-lg font-semibold text-navy">Vendors</h2>
        <div role="alert" data-testid="vendors-no-access" className="mt-4 p-4 rounded border bg-amber-50 text-amber-900 text-sm">
          {unavailable ? 'Vendors is not available right now. Nothing was changed; try again in a moment.' : 'Your account does not have access to Vendors. A Company Admin can give it to you (View, Maintain or Administer Vendors).'}
        </div>
      </div>
    );
  }
  const level = me.level;
  const TABS = ALL_TABS.filter((t) => atLeast(level, t.need));
  const shownView: View = TABS.some((t) => t.id === view) ? view : 'vendors';
  // keyboard: Left / Right (wrapping), Home and End move between the tabs and select the one reached (the tabs follow focus because switching is instant and loses nothing)
  const onKey = (e: React.KeyboardEvent) => {
    const i = TABS.findIndex((t) => t.id === shownView);
    const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i + TABS.length - 1) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault(); setView(TABS[next].id); tabRefs.current[TABS[next].id]?.focus();
  };
  // LOCAL IMPORT REHEARSAL ONLY: set by the rehearsal launcher (VITE_LOCAL_IMPORT_REHEARSAL). A normal build never sets it, so nothing is shown.
  const rehearsal = (import.meta.env.VITE_LOCAL_IMPORT_REHEARSAL as string | undefined) || '';
  return (
    <div className="p-6" data-testid="vendors-page">
      <p className="mb-3 text-xs text-gray-500" data-testid="vendors-your-access">Your access: <strong>{me.companyAdmin ? 'Company Admin (full access)' : LEVEL_LABEL[level]}</strong></p>
      {rehearsal && (
        <div role="note" data-testid="import-rehearsal-banner" className="mb-4 p-3 rounded border border-amber-400 bg-amber-50 text-amber-900 text-sm">
          <strong>{rehearsal}</strong>: this is a separate, disposable copy of the Owner's vendor files. Nothing here is in DEV or in your real directory. Imported entries are held as <strong>Needs review</strong>.{' '}
          <a data-testid="import-rehearsal-report-link" className="underline font-medium" href={`${config.apiUrl}/local/rehearsal`} target="_blank" rel="noreferrer">Open the reconciliation report</a>
        </div>
      )}
      {/* Folder tabs: the selected tab is white, open at the bottom and sits over the top border of the content below it, so it reads as one piece with its content. On a narrow screen the row scrolls sideways instead of wrapping. */}
      <div role="tablist" aria-label="Vendors sections" data-testid="vendors-view-switch" onKeyDown={onKey} className="flex items-end gap-1 overflow-x-auto pl-2 -mb-px relative z-10">
        {TABS.map((t) => {
          const on = shownView === t.id;
          return (
            <button key={t.id} ref={(el) => { tabRefs.current[t.id] = el; }} type="button" role="tab" id={`vendors-tab-${t.id}`} aria-selected={on} aria-controls="vendors-tabpanel" tabIndex={on ? 0 : -1}
              data-testid={`vendors-view-${t.id}`} onClick={() => setView(t.id)}
              className={`whitespace-nowrap px-5 py-2 text-sm font-medium border rounded-t-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${on ? 'bg-white text-navy border-gray-300 border-b-white pb-2.5' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 mt-1'}`}>
              {t.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id="vendors-tabpanel" aria-labelledby={`vendors-tab-${shownView}`} data-testid={`vendors-tabpanel-${shownView}`} className="bg-white border border-gray-300 rounded-b-lg rounded-tr-lg p-5">
        {shownView === 'vendors' && <VendorsPanel deepLink={link} level={level} />}
        {shownView === 'expiring' && <ExpiringView onOpen={(vendorId, coverageId) => { setLink({ vendorId, coverageId, n: Date.now() }); setView('vendors'); }} />}
        {shownView === 'admin' && <VendorsPanel mode="admin" level={level} canGrant={me.canGrant} />}
      </div>
    </div>
  );
}
